import { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Badge, Button, Modal, Form, Input, Select, Switch, Space, message,
} from 'antd';
import { users } from '../../services/api';

const ROLE_MAP = {
  admin: { text: '系统管理员', color: 'red' },
  operator: { text: '运营人员', color: 'blue' },
  viewer: { text: '只读查看', color: 'default' },
};

export default function UsersAdminPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const load = async (username) => {
    try {
      const res = await users.list(username ? { username } : undefined);
      setList(res.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      await users.create(values);
      message.success('用户已创建');
      setCreateOpen(false);
      createForm.resetFields();
      load(keyword);
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();
      await users.update(editUser.id, values);
      message.success('用户信息已更新');
      setEditUser(null);
      load(keyword);
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const handleResetPassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      await users.resetPassword(pwdUser.id, { password: values.password });
      message.success('密码已重置');
      setPwdUser(null);
      pwdForm.resetFields();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const openEdit = (record) => {
    setEditUser(record);
    editForm.setFieldsValue({ name: record.name, role: record.role, is_active: !!record.is_active });
  };

  const roleOptions = (
    <Select>
      <Select.Option value="admin">系统管理员</Select.Option>
      <Select.Option value="operator">运营人员</Select.Option>
      <Select.Option value="viewer">只读查看</Select.Option>
    </Select>
  );

  const columns = [
    { title: '用户名', dataIndex: 'username' },
    { title: '姓名', dataIndex: 'name' },
    {
      title: '角色', dataIndex: 'role',
      render: (r) => <Tag color={ROLE_MAP[r]?.color}>{ROLE_MAP[r]?.text || r}</Tag>,
    },
    {
      title: '状态', dataIndex: 'is_active',
      render: (s) => (s ? <Badge status="success" text="启用" /> : <Badge status="default" text="禁用" />),
    },
    {
      title: '创建时间', dataIndex: 'created_at',
      render: (t) => (t ? new Date(t).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作', key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => setPwdUser(record)}>重置密码</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="用户管理"
        size="small"
        extra={
          <Space>
            <Input.Search
              placeholder="按用户名搜索"
              allowClear
              style={{ width: 220 }}
              onSearch={(v) => { setKeyword(v); load(v); }}
            />
            <Button type="primary" onClick={() => setCreateOpen(true)}>新建用户</Button>
          </Space>
        }
      >
        <Table
          loading={loading}
          columns={columns}
          dataSource={list.map((u) => ({ ...u, key: u.id }))}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal title="新建用户" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={handleCreate} okText="创建">
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="username" label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { pattern: /^\w{3,20}$/, message: '3-20 位字母、数字或下划线' },
            ]}
          >
            <Input placeholder="3-20 位字母、数字或下划线" />
          </Form.Item>
          <Form.Item
            name="password" label="初始密码"
            rules={[
              { required: true, message: '请输入初始密码' },
              { min: 8, message: '密码长度不能少于 8 位' },
            ]}
          >
            <Input.Password placeholder="至少 8 位" />
          </Form.Item>
          <Form.Item name="name" label="姓名"><Input /></Form.Item>
          <Form.Item name="role" label="角色" initialValue="operator" rules={[{ required: true }]}>
            {roleOptions}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑用户：${editUser?.username || ''}`}
        open={!!editUser} onCancel={() => setEditUser(null)} onOk={handleEdit} okText="保存"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}><Input /></Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>{roleOptions}</Form.Item>
          <Form.Item name="is_active" label="启用状态" valuePropName="checked"><Switch checkedChildren="启用" unCheckedChildren="禁用" /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`重置密码：${pwdUser?.username || ''}`}
        open={!!pwdUser} onCancel={() => setPwdUser(null)} onOk={handleResetPassword} okText="重置"
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            name="password" label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码长度不能少于 8 位' },
            ]}
          >
            <Input.Password placeholder="至少 8 位" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
